import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { RuleFolder as DiffIcon } from "@mui/icons-material";

import { usePageTitle } from "../context/PageTitleContext";
import {
  useDiffsList,
  useUpdateDiffReason,
} from "../hooks/useDistributionAnalytics";
import {
  DIFF_REASON_OPTIONS,
  DiffReason,
} from "../services/distributionService";

/**
 * B3.35 — DiferenciasTransferenciasPage
 *
 * Lista `transfer_diff` pendientes de captura (`reason=NULL`). Cada
 * fila tiene dropdown con las 12 razones; al seleccionar se hace
 * PUT y la fila desaparece de la lista (o queda en modo auditoría
 * si toggle activado).
 */
export function DiferenciasTransferenciasPage(): React.ReactElement {
  const { setTitle } = usePageTitle();
  const [showAudited, setShowAudited] = useState(false);
  const [store, setStore] = useState("");
  const [product, setProduct] = useState("");

  useEffect(() => {
    setTitle("Distribución — Diferencias");
    return () => setTitle("");
  }, [setTitle]);

  const params = useMemo(() => {
    const p: Parameters<typeof useDiffsList>[0] = { limit: 200 };
    if (showAudited) p.reason = "";
    if (store) p.store = store.toUpperCase();
    if (product) p.product_id = product;
    return p;
  }, [showAudited, store, product]);

  const { data, isLoading } = useDiffsList(params);
  const update = useUpdateDiffReason();
  const items = data?.items ?? [];

  async function handleReasonChange(
    id: number,
    reason: DiffReason,
  ): Promise<void> {
    await update.mutateAsync({ id, reason });
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <DiffIcon color="primary" />
          <Typography variant="h4" component="h1" fontWeight={600}>
            Diferencias de transferencias
          </Typography>
        </Stack>

        <Alert severity="info" variant="outlined">
          Captura de razones residuales cuando el auto-clasificador no pudo
          aplicar R1–R5. Al seleccionar la razón, la fila se cierra.
          <br />
          <strong>Sin texto libre.</strong>
        </Alert>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              label="Tienda"
              value={store}
              onChange={(e) => setStore(e.target.value)}
              placeholder="SLP04"
            />
            <TextField
              size="small"
              label="Producto"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showAudited}
                  onChange={(e) => setShowAudited(e.target.checked)}
                />
              }
              label="Modo auditoría (incluir auto-clasificados)"
            />
          </Stack>
        </Paper>

        <Paper variant="outlined">
          {isLoading && (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          )}
          {!isLoading && items.length === 0 && (
            <Alert severity="success" sx={{ m: 2 }}>
              Sin diferencias pendientes de captura para estos filtros.
            </Alert>
          )}
          {items.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Creado</TableCell>
                    <TableCell>Tienda</TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Planeada</TableCell>
                    <TableCell align="right">Entregada</TableCell>
                    <TableCell align="right">Diff</TableCell>
                    <TableCell>Razón</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((d) => (
                    <TableRow key={d.id} hover>
                      <TableCell>
                        {new Date(d.created_at).toLocaleString("es-MX")}
                      </TableCell>
                      <TableCell>{d.store_mxp_code}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {d.unified_product_id}
                      </TableCell>
                      <TableCell align="right">{d.qty_planned}</TableCell>
                      <TableCell align="right">{d.qty_delivered}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color:
                            Number(d.diff) < 0 ? "warning.main" : "info.main",
                          fontWeight: 600,
                        }}
                      >
                        {d.diff}
                      </TableCell>
                      <TableCell>
                        {d.reason ? (
                          <Chip
                            size="small"
                            label={d.reason}
                            color={d.auto_resolved ? "info" : "success"}
                            variant={d.auto_resolved ? "outlined" : "filled"}
                          />
                        ) : (
                          <Select
                            size="small"
                            value=""
                            onChange={(e) =>
                              void handleReasonChange(
                                d.id,
                                e.target.value as DiffReason,
                              )
                            }
                            disabled={update.isPending}
                            displayEmpty
                            sx={{ minWidth: 240 }}
                          >
                            <MenuItem value="" disabled>
                              Seleccionar razón...
                            </MenuItem>
                            {DIFF_REASON_OPTIONS.map((r) => (
                              <MenuItem key={r} value={r}>
                                {r}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {data && (
            <Box sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {items.length} diffs mostrados. Total filtrados: {data.total}.
              </Typography>
            </Box>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
